import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
	Component,
	TemplateRef,
	computed,
	contentChildren,
	effect,
	input,
	signal,
	untracked,
} from '@angular/core';

@Component({
	selector: 'ae-carousel',
	imports: [NgClass, NgTemplateOutlet],
	templateUrl: './carousel.component.html',
	styleUrl: './carousel.component.scss',
})
export class CarouselComponent {
	public items = input.required<any[]>();
	public selectedIndex = input<number | null>(null);
	public navigationItemLabel = input<
		string | ((item: any, index: number) => string | null) | null
	>(null);
	protected currentIndex = signal(0);
	protected slideTemplates = contentChildren(TemplateRef<unknown>);
	protected totalSlides = computed(() => this.items().length);
	protected activeIndex = computed(() => {
		const totalSlides = this.totalSlides();

		if (!totalSlides) {
			return 0;
		}

		return Math.min(this.currentIndex(), totalSlides - 1);
	});
	protected activeItem = computed(
		() => this.items()[this.activeIndex()] ?? null,
	);
	protected previousIndex = computed(() => {
		return this.getIndex();
	});
	protected nextIndex = computed(() => {
		return this.getIndex(true);
	});
	protected previousButtonTitle = computed(() => {
		return this.resolveNavigationTitle(this.previousIndex(), 'Previous Slide');
	});
	protected nextButtonTitle = computed(() => {
		return this.resolveNavigationTitle(this.nextIndex(), 'Next Slide');
	});
	protected activeTemplate = computed(() => {
		const templates = this.slideTemplates();

		if (!templates.length) {
			return null;
		}

		return templates[0];
	});

	constructor() {
		// This cannot be called in ngOnInit
		effect(() => {
			const totalSlides = this.totalSlides();
			const selectedIndex = this.selectedIndex();

			if (!totalSlides) {
				const currentIndex = untracked(() => this.currentIndex());

				if (currentIndex !== 0) {
					this.currentIndex.set(0);
				}

				return;
			}

			if (selectedIndex != null) {
				const nextIndex = Math.max(0, Math.min(selectedIndex, totalSlides - 1));
				const currentIndex = untracked(() => this.currentIndex());

				if (currentIndex !== nextIndex) {
					this.currentIndex.set(nextIndex);
				}

				return;
			}

			const currentIndex = untracked(() => this.currentIndex());

			if (currentIndex >= totalSlides) {
				this.currentIndex.set(totalSlides - 1);
			}
		});
	}

	protected showControls(): boolean {
		return this.totalSlides() > 1;
	}

	protected previous(): void {
		const totalSlides = this.totalSlides();

		if (!totalSlides) {
			return;
		}

		this.currentIndex.update((index) => {
			return index === 0 ? totalSlides - 1 : index - 1;
		});
	}

	protected next(): void {
		const totalSlides = this.totalSlides();

		if (!totalSlides) {
			return;
		}

		this.currentIndex.update((index) => {
			return index === totalSlides - 1 ? 0 : index + 1;
		});
	}

	protected selectSlide(index: number): void {
		const totalSlides = this.totalSlides();

		if (index < 0 || index >= totalSlides) {
			return;
		}

		this.currentIndex.set(index);
	}

	private resolveNavigationTitle(index: number, fallback: string): string {
		const navigationItemLabel = this.navigationItemLabel();

		if (!navigationItemLabel) {
			return fallback;
		}

		const item = this.items()[index];

		if (!item) {
			return fallback;
		}

		const label =
			typeof navigationItemLabel === 'function'
				? navigationItemLabel(item, index)
				: item[navigationItemLabel];

		return typeof label === 'string' && label.trim() ? label : fallback;
	}

	private getIndex = (next?: boolean) => {
		const totalSlides = this.totalSlides();

		if (!totalSlides) {
			return 0;
		}

		const currentIndex = this.activeIndex();

		if (next) {
			return currentIndex === totalSlides - 1 ? 0 : currentIndex + 1;
		}

		return currentIndex === 0 ? totalSlides - 1 : currentIndex - 1;
	};
}
